import { device } from 'utils/device';
import { SineLookup } from 'particles/lookups/SineLookup';
import { IsometricGridLayout } from 'particles/layouts/IsometricGridLayout';
import {
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { SINE_NORMAL, sineKernel } from 'app/views/home/webgl/particlesOptimised/core/kernels/sine';

import type { SineFrame } from 'app/views/home/webgl/particlesOptimised/core/kernels/sine';
import type { SpacesContext, FrameSizes } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * SineGridState — the ONE sine state machine the three sine-grid phases
 * share, exactly like the reference (the _sineIndex/_xIncrease/... fields
 * live once on SpacesDepartment there, so the wave's phase angle CARRIES
 * ACROSS the three stages and across cycles — three per-phase copies would
 * restart the wave at each stage). SpacesDepartment creates one instance
 * and hands it to the three phase files.
 *
 * It also owns the one-time flattened tables (the sine lookup's y channel +
 * the 42x50 isometric grid the reference constructor builds) and the
 * per-frame kernel dispatch.
 */
export class SineGridState {
	//The tuning the concrete phases write on arm (reference field defaults):
	public xIncrease = 5;
	public maxSineX = 10;
	public minSineX = -10;
	public sineDirection = 1;
	public sineYChange = 0.13;
	public sinePlaySeed = 2.5;
	public sineType: number = SINE_NORMAL;
	//_sineIndex — deliberately NEVER reset by the arms (the wave angle
	//carries across stages/cycles/scene stops, reference behaviour):
	public sineIndex = 0;

	public sineCount = 0;
	public sineY: Float32Array;
	public isoX: Float32Array;
	public isoY: Float32Array;

	private _initDone = false;
	private _frameSizes: FrameSizes = { x1: 0, x2: 0, width: 0, xc: 0, y1: 0, y2: 0, height: 0, yc: 0 };
	private _sineFrame: SineFrame = {
		type: SINE_NORMAL,
		xIncrease: 5,
		roundedIndex: 0,
		total: 0,
		frameX1: 0,
		frameX2: 0,
		innerHeight: 1,
		sineSeed: 5
	};

	/** One-time table build (any of the three phases' init() triggers it). */
	public init(ctx: SpacesContext): void {
		if (this._initDone) {
			return;
		}
		this._initDone = true;

		//Flatten the sine lookup's y channel (constructor-built, immutable):
		const sineTable = new SineLookup().table;
		this.sineY = new Float32Array(sineTable.length);
		for (let i = 0; i < sineTable.length; i++) {
			this.sineY[i] = sineTable[i].y;
		}

		//The isometric grid positions (reference ctor: layouts.isometric.get
		//with the same args — count is ignored by the layout, which always
		//emits its 42x50 grid), flattened once:
		const iso = new IsometricGridLayout().get(ctx.activeCount, { x: -0.05, y: 0.2 }, { w: 0.04, h: 0.5 });
		this.isoX = new Float32Array(iso.length);
		this.isoY = new Float32Array(iso.length);
		for (let i = 0; i < iso.length; i++) {
			this.isoX[i] = iso[i].x;
			this.isoY[i] = iso[i].y;
		}

		//_spaceSineParticles = pool.slice(0, iso.length):
		this.sineCount = Math.min(iso.length, ctx.activeCount);
	}

	/** _processSineWave: the per-frame state machine + the sine kernel over
	 *  the sine lanes (core/kernels/sine.ts — the drag-profile hotspot). */
	public processSineWave(ctx: SpacesContext): void {
		//Reuse one frame-sizes object (hot path — reference quick-wins):
		ctx.getFrameSizes(this._frameSizes);

		const deltaTime = ctx.deps.context.deltaTime;

		this.sineIndex += deltaTime * this.sinePlaySeed;

		this.xIncrease += deltaTime * this.sineYChange * this.sineDirection;

		if (this.xIncrease > this.maxSineX || this.xIncrease < this.minSineX) {
			this.sineDirection *= -1;
		}

		//(the reference's _xCrestPointer flip is dead state — the pointer is
		//never advanced or read; not ported.)

		if (this.sineIndex >= this.sineY.length) {
			this.sineIndex = 0;
		}

		const frame = this._sineFrame;
		frame.type = this.sineType;
		frame.xIncrease = this.xIncrease;
		frame.roundedIndex = Math.round(this.sineIndex);
		frame.total = this.sineCount;
		frame.frameX1 = this._frameSizes.x1;
		frame.frameX2 = this._frameSizes.x2;
		frame.innerHeight = device.innerHeight;
		frame.sineSeed = 5;

		sineKernel(ctx.core, ctx.range.start, this.isoX, this.isoY, this.sineY, frame);
	}
}

/**
 * SineGridPhase — the shared base of the three sine-grid phase files: FORK
 * of SpacesDepartment._startSine + _processSineWave. The concrete phases
 * (Normal/Random/Wavey) supply only their tuning constants and arm order —
 * mirroring the reference's three thin _sineWaveGrid* methods over one
 * shared engine (SineGridState above).
 *
 * VISUAL: the first ~2100 particles sit on an isometric grid across the box
 * and undulate as a travelling sine wave — dot y and scale ride the wave
 * (bigger on crests); a 200px PUSH repell lets the mouse part the field.
 *
 * Parity note: the reference pass lives in department-private methods on a
 * class that cannot be instantiated headlessly (its constructor needs a
 * live HomeField + full sceneProps bag), so the sine kernel is verified
 * STATISTICALLY against the live reference scene (band amplitude /
 * wavelength / phase envelopes) instead of unit head-to-head — see the
 * stage report.
 */
export abstract class SineGridPhase implements DepartmentPhase {
	public abstract readonly name: string;

	protected _ctx: SpacesContext;
	protected _state: SineGridState;

	private _processSine = false;

	constructor(ctx: SpacesContext, state: SineGridState) {
		this._ctx = ctx;
		this._state = state;
	}

	public init(): void {
		this._state.init(this._ctx);
	}

	/** The concrete phase's tuning + arm order (see each file). */
	public abstract start(): void;

	public tick(): void {
		if (!this._processSine) {
			return;
		}

		this._state.processSineWave(this._ctx);
	}

	public hide(): void {
		//_processSine = false at every reference stage change:
		this._processSine = false;
	}

	public reset(): void {
		//The shared SineGridState persists across stops on purpose (its
		//fields live on the reference department and are never reset).
	}

	/** _startSine: arm the sine lanes, hide the leftovers. */
	protected startSine(): void {
		const ctx = this._ctx;
		const core = ctx.core;
		this._processSine = true;

		for (let n = 0; n < this._state.sineCount; n++) {
			const lane = ctx.range.start + n;

			ctx.deactivateAll(lane);
			//activate():
			core.stateFlags[lane] |= FLAG_ACTIVE;

			writeVectorConfig(core, lane, VEC_X, false);
			writeVectorConfig(core, lane, VEC_Y, false);
			//setEase({y: random(10, 20)}) — y only:
			core.easeY[lane] = 10 + Math.random() * 20;
			core.easeLerpFramesY[lane] = 0;
			Math.random(); //sineSeed = random(0.4, 0.8) — dead roll kept (overwritten by the constant 5)

			core.tAlpha[lane] = 0.35 + Math.random() * 0.35;

			//baseScale 0.15, second activate(), position.active = true (already):
			core.scale[lane] = 0.15;
			core.tScale[lane] = 0.15;
			core.tScaleSaved[lane] = 0.15;

			core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
			core.repellType[lane] = REPELL_PUSH;
			core.repellRadius[lane] = 200;
			core.repellPower[lane] = 100 + Math.random() * 200;
		}

		for (let n = this._state.sineCount; n < ctx.activeCount; n++) {
			ctx.hideLane(ctx.range.start + n);
		}
	}
}
